import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppInput,
  AppText,
} from '../../ui-foundation';
import {
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
} from '../../ui-foundation/adapters/web';

export interface RecurrenceData {
  recurrence_type: 'one-off' | 'custom';
  recurrence_interval: number | null;
  recurrence_unit: 'days' | 'weeks' | 'months' | 'years' | null;
  recurrence_day_of_week: number[] | null; // 0=Mon..6=Sun
  recurrence_week_of_month: string | null; // e.g. 'first', 'second', 'third', 'fourth', 'last'
  recurrence_month_day: number | null; // 1-31
}

export const DEFAULT_RECURRENCE: RecurrenceData = {
  recurrence_type: 'one-off',
  recurrence_interval: null,
  recurrence_unit: null,
  recurrence_day_of_week: null,
  recurrence_week_of_month: null,
  recurrence_month_day: null,
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const UNITS = ['days', 'weeks', 'months', 'years'] as const;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const ORDINALS = [
  { value: 'first', label: 'First' },
  { value: 'second', label: 'Second' },
  { value: 'third', label: 'Third' },
  { value: 'fourth', label: 'Fourth' },
  { value: 'last', label: 'Last' },
];

function buildSummary(d: RecurrenceData): string {
  if (d.recurrence_type === 'one-off') return '';
  if (!d.recurrence_interval || !d.recurrence_unit) return '';

  const interval = d.recurrence_interval;
  const unit = d.recurrence_unit;
  let base = interval === 1 ? `Every ${unit.slice(0, -1)}` : `Every ${interval} ${unit}`;

  if (unit === 'weeks' && d.recurrence_day_of_week?.length) {
    const dayNames = d.recurrence_day_of_week.sort().map(i => DAYS[i]);
    base += ` on ${dayNames.join(' and ')}`;
  }

  if (unit === 'months') {
    if (d.recurrence_week_of_month && d.recurrence_day_of_week?.length) {
      const ordinal = ORDINALS.find(o => o.value === d.recurrence_week_of_month)?.label || '';
      const dayName = DAYS[d.recurrence_day_of_week[0]];
      base += ` on the ${ordinal.toLowerCase()} ${dayName}`;
    } else if (d.recurrence_month_day) {
      base += ` on day ${d.recurrence_month_day}`;
    }
  }

  return base;
}

interface Props {
  value: RecurrenceData;
  onChange: (data: RecurrenceData) => void;
}

export default function RecurrenceBuilder({ value, onChange }: Props) {
  const isRecurring = value.recurrence_type === 'custom';

  const setField = <K extends keyof RecurrenceData>(key: K, val: RecurrenceData[K]) => {
    onChange({ ...value, [key]: val });
  };

  const toggleRecurring = (recurring: boolean) => {
    if (recurring) {
      onChange({ ...value, recurrence_type: 'custom', recurrence_interval: 1, recurrence_unit: 'months' });
    } else {
      onChange(DEFAULT_RECURRENCE);
    }
  };

  const toggleDayOfWeek = (dayIdx: number) => {
    const current = value.recurrence_day_of_week || [];
    const next = current.includes(dayIdx) ? current.filter(d => d !== dayIdx) : [...current, dayIdx];
    setField('recurrence_day_of_week', next.length > 0 ? next : null);
  };

  const summary = buildSummary(value);
  const monthlyDaySelected = !value.recurrence_week_of_month && Boolean(value.recurrence_month_day);
  const monthlyOrdinalSelected = Boolean(value.recurrence_week_of_month);

  return (
    <AppCard backgroundColor="$background" px="$sm" py="$sm" gap="$sm">
      <AppButtonRow>
        <AppButton type="button" flex={1} size="sm" tone={!isRecurring ? 'primary' : 'outline'} onPress={() => toggleRecurring(false)}>
          One-off
        </AppButton>
        <AppButton type="button" flex={1} size="sm" tone={isRecurring ? 'primary' : 'outline'} onPress={() => toggleRecurring(true)}>
          Recurring
        </AppButton>
      </AppButtonRow>

      {isRecurring ? (
        <AppCardBody className="animate-fade-in">
          <AppFieldGroup label="Frequency">
            <AppButtonRow alignItems="stretch">
              <AppInput
                width={72}
                tag="input"
                type="number"
                min={1}
                max={99}
                value={String(value.recurrence_interval || 1)}
                onChange={(event) => {
                  const nextValue = parseInt((event.target as HTMLInputElement).value, 10) || 1;
                  setField('recurrence_interval', Math.max(1, Math.min(99, nextValue)));
                }}
              />
              <AppButtonRow flex={1}>
                {UNITS.map((u) => (
                  <AppButton
                    key={u}
                    type="button"
                    size="sm"
                    tone={value.recurrence_unit === u ? 'secondary' : 'outline'}
                    onPress={() => {
                      onChange({
                        ...value,
                        recurrence_unit: u,
                        recurrence_day_of_week: null,
                        recurrence_week_of_month: null,
                        recurrence_month_day: null,
                      });
                    }}
                  >
                    {u.charAt(0).toUpperCase() + u.slice(1)}
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppButtonRow>
          </AppFieldGroup>

          {value.recurrence_unit === 'weeks' ? (
            <AppFieldGroup label="On">
              <AppButtonRow>
                {DAYS.map((d, i) => (
                  <AppButton
                    key={d}
                    type="button"
                    size="sm"
                    minWidth={48}
                    tone={(value.recurrence_day_of_week || []).includes(i) ? 'secondary' : 'outline'}
                    onPress={() => toggleDayOfWeek(i)}
                  >
                    {d}
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppFieldGroup>
          ) : null}

          {value.recurrence_unit === 'months' ? (
            <AppFieldGroup label="On">
              <AppCard
                backgroundColor="$background"
                borderColor={monthlyDaySelected ? '$primary' : '$borderColor'}
                px="$sm"
                py="$sm"
              >
                <AppCardBody>
                  <AppButton
                    type="button"
                    alignSelf="flex-start"
                    size="sm"
                    tone={monthlyDaySelected ? 'secondary' : 'outline'}
                    onPress={() => onChange({
                      ...value,
                      recurrence_month_day: value.recurrence_month_day || 1,
                      recurrence_week_of_month: null,
                      recurrence_day_of_week: null,
                    })}
                  >
                    Day of month
                  </AppButton>
                  <AppButtonRow alignItems="center">
                    <AppText fontSize="$sm">Day</AppText>
                    <AppInput
                      width={72}
                      tag="input"
                      type="number"
                      min={1}
                      max={31}
                      value={String(value.recurrence_month_day || 1)}
                      onChange={(event) => onChange({
                        ...value,
                        recurrence_month_day: parseInt((event.target as HTMLInputElement).value, 10) || 1,
                        recurrence_week_of_month: null,
                        recurrence_day_of_week: null,
                      })}
                    />
                    <AppText fontSize="$sm" color="$colorMuted">of the month</AppText>
                  </AppButtonRow>
                </AppCardBody>
              </AppCard>

              <AppCard
                backgroundColor="$background"
                borderColor={monthlyOrdinalSelected ? '$primary' : '$borderColor'}
                px="$sm"
                py="$sm"
              >
                <AppCardBody>
                  <AppButton
                    type="button"
                    alignSelf="flex-start"
                    size="sm"
                    tone={monthlyOrdinalSelected ? 'secondary' : 'outline'}
                    onPress={() => onChange({
                      ...value,
                      recurrence_week_of_month: value.recurrence_week_of_month || 'first',
                      recurrence_day_of_week: value.recurrence_day_of_week || [0],
                      recurrence_month_day: null,
                    })}
                  >
                    Ordinal weekday
                  </AppButton>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Select
                      value={value.recurrence_week_of_month || 'first'}
                      onValueChange={(nextValue) => onChange({
                        ...value,
                        recurrence_week_of_month: nextValue,
                        recurrence_month_day: null,
                        recurrence_day_of_week: value.recurrence_day_of_week || [0],
                      })}
                    >
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Week" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDINALS.map((ordinal) => (
                          <SelectItem key={ordinal.value} value={ordinal.value}>
                            {ordinal.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={String((value.recurrence_day_of_week || [0])[0])}
                      onValueChange={(nextValue) => onChange({
                        ...value,
                        recurrence_day_of_week: [parseInt(nextValue, 10)],
                        recurrence_month_day: null,
                        recurrence_week_of_month: value.recurrence_week_of_month || 'first',
                      })}
                    >
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Day" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map((day, index) => (
                          <SelectItem key={day} value={String(index)}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </AppCardBody>
              </AppCard>
            </AppFieldGroup>
          ) : null}

          {value.recurrence_unit === 'years' ? (
            <AppFieldGroup label="On date">
              <div className="grid gap-2 sm:grid-cols-2">
                <Select
                  value={String(value.recurrence_month_day || 1)}
                  onValueChange={(nextValue) => setField('recurrence_month_day', parseInt(nextValue, 10))}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={String((value.recurrence_day_of_week || [0])[0])}
                  onValueChange={(nextValue) => setField('recurrence_day_of_week', [parseInt(nextValue, 10)])}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, index) => (
                      <SelectItem key={month} value={String(index)}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </AppFieldGroup>
          ) : null}

          {summary ? (
            <AppText fontSize="$sm" fontWeight="700" color="$secondary">
              {summary}
            </AppText>
          ) : null}
        </AppCardBody>
      ) : null}
    </AppCard>
  );
}

export { buildSummary };
